<script setup lang="ts">
import type { DropdownValue } from '@/types/dropdown'

import type { DelaySettings, DelayUnit } from '../types/marketingFlow'

const { t } = useI18n()
const { isDelayStepCompleted, getDelayStepErrors } = useMarketingFlow()

const props = defineProps<{
  modelValue: DelaySettings
  triggerType?: string | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: DelaySettings): void
  (e: 'goNextStep'): void
  (e: 'goPreviousStep'): void
  (e: 'onChanges', change: { isCritical: boolean; field?: string; value?: any }): void
}>()

const hourSelected = ref<number>(0)
const delayUnit = ref<number>(1)

const hoursOptions: (DropdownValue & { label: string })[] = [
  { id: 1, text: '0:00', label: '0h00' },
  { id: 2, text: '1:00', label: '1h00' },
  { id: 3, text: '2:00', label: '2h00' },
  { id: 4, text: '3:00', label: '3h00' },
  { id: 5, text: '4:00', label: '4h00' },
  { id: 6, text: '5:00', label: '5h00' },
  { id: 7, text: '6:00', label: '6h00' },
  { id: 8, text: '7:00', label: '7h00' },
  { id: 9, text: '8:00', label: '8h00' },
  { id: 10, text: '9:00', label: '9h00' },
  { id: 11, text: '10:00', label: '10h00' },
  { id: 12, text: '11:00', label: '11h00' },
  { id: 13, text: '12:00', label: '12h00' },
  { id: 14, text: '13:00', label: '13h00' },
  { id: 15, text: '14:00', label: '14h00' },
  { id: 16, text: '15:00', label: '15h00' },
  { id: 17, text: '16:00', label: '16h00' },
  { id: 18, text: '17:00', label: '17h00' },
  { id: 19, text: '18:00', label: '18h00' },
  { id: 20, text: '19:00', label: '19h00' },
  { id: 21, text: '20:00', label: '20h00' },
  { id: 22, text: '21:00', label: '21h00' },
  { id: 23, text: '22:00', label: '22h00' },
  { id: 24, text: '23:00', label: '23h00' }
]

const birthdayHoursOptions: (DropdownValue & { label: string })[] = [
  { id: 1, text: '7:00', label: '7h00' },
  { id: 2, text: '7:30', label: '7h30' },
  { id: 3, text: '8:00', label: '8h00' },
  { id: 4, text: '8:30', label: '8h30' },
  { id: 5, text: '9:00', label: '9h00' },
  { id: 6, text: '9:30', label: '9h30' },
  { id: 7, text: '10:00', label: '10h00' },
  { id: 8, text: '10:30', label: '10h30' },
  { id: 9, text: '11:00', label: '11h00' },
  { id: 10, text: '11:30', label: '11h30' },
  { id: 11, text: '12:00', label: '12h00' },
  { id: 12, text: '12:30', label: '12h30' },
  { id: 13, text: '13:00', label: '13h00' },
  { id: 14, text: '13:30', label: '13h30' },
  { id: 15, text: '14:00', label: '14h00' },
  { id: 16, text: '14:30', label: '14h30' },
  { id: 17, text: '15:00', label: '15h00' },
  { id: 18, text: '15:30', label: '15h30' },
  { id: 19, text: '16:00', label: '16h00' },
  { id: 20, text: '16:30', label: '16h30' },
  { id: 21, text: '17:00', label: '17h00' },
  { id: 22, text: '17:30', label: '17h30' },
  { id: 23, text: '18:00', label: '18h00' },
  { id: 24, text: '18:30', label: '18h30' },
  { id: 25, text: '19:00', label: '19h00' },
  { id: 26, text: '19:30', label: '19h30' },
  { id: 27, text: '20:00', label: '20h00' }
]

const delayUnits: (DropdownValue & { label: string })[] = [
  { id: 1, text: t('global.words.daysCapitalized'), label: t('global.words.daysCapitalized') },
  { id: 2, text: t('global.words.hoursCapitalized'), label: t('global.words.hoursCapitalized') },
  { id: 3, text: t('global.words.minutesCapitalized'), label: t('global.words.minutesCapitalized') }
]

const currentHoursOptions = computed(() => {
  return props.triggerType === 'BIRTHDAY' ? birthdayHoursOptions : hoursOptions
})

const delayStepErrors = computed(() => {
  return getDelayStepErrors(props.modelValue, props.triggerType)
})

const daysBeforeActionError = computed(() => {
  const daysBeforeActionError = delayStepErrors.value.find((error) => error.field === 'daysBeforeAction')
  return daysBeforeActionError && props.modelValue.daysBeforeAction !== null
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${daysBeforeActionError.i18nKey}`)]
    : null
})

const handleUpdateSendImmediatlyAfterTrigger = (e: boolean) => {
  let sendAtSpecificHourValue = props.modelValue.sendAtSpecificHour
  let sendAfterDelayValue = props.modelValue.sendAfterDelay
  if (e && props.modelValue.sendAtSpecificHour) {
    sendAtSpecificHourValue = false
  }
  if (e && props.modelValue.sendAfterDelay) {
    sendAfterDelayValue = false
  }

  emit('update:modelValue', {
    ...props.modelValue,
    sendImmediatlyAfterTrigger: e,
    sendAtSpecificHour: sendAtSpecificHourValue,
    sendAfterDelay: sendAfterDelayValue
  })
  emit('onChanges', { isCritical: true, field: 'sendImmediatlyAfterTrigger', value: e })
}

const handleUpdateSendAfterDelay = (e: boolean) => {
  let sendImmediatlyAfterTriggerValue = props.modelValue.sendImmediatlyAfterTrigger
  if (e && sendImmediatlyAfterTriggerValue) sendImmediatlyAfterTriggerValue = false

  emit('update:modelValue', {
    ...props.modelValue,
    sendImmediatlyAfterTrigger: sendImmediatlyAfterTriggerValue,
    sendAfterDelay: e
  })
  emit('onChanges', { isCritical: true, field: 'sendAfterDelay', value: e })
}

const handleUpdateDaysBeforeAction = (e: number) => {
  emit('update:modelValue', { ...props.modelValue, daysBeforeAction: e })
  emit('onChanges', { isCritical: true, field: 'daysBeforeAction', value: e })
}

const handleUpdateSendAtSpecificHour = (e: boolean) => {
  emit('update:modelValue', { ...props.modelValue, sendAtSpecificHour: e })
  emit('onChanges', { isCritical: true, field: 'sendAtSpecificHour', value: e })
}

const handleUpdateHourToSend = (e) => {
  if (e) {
    hourSelected.value = e

    const selectedHourOption = currentHoursOptions.value.find((option) => option.id === e)

    emit('update:modelValue', {
      ...props.modelValue,
      hourToSend: selectedHourOption?.text,
      hourToSendLabel: selectedHourOption?.label
    })
    emit('onChanges', { isCritical: true, field: 'hourToSend', value: selectedHourOption?.text })
  }
}

const handleUpdateDelayUnit = (e) => {
  if (e) {
    let sendAtSpecificHourValue = props.modelValue.sendAtSpecificHour
    let hourToSendValue = props.modelValue.hourToSend

    delayUnit.value = e
    let delayUnitValue: DelayUnit = 'DAYS'
    if (e === 2) {
      delayUnitValue = 'HOURS'
      sendAtSpecificHourValue = false
      hourToSendValue = null
    } else if (e === 3) {
      delayUnitValue = 'MINUTES'
      sendAtSpecificHourValue = false
      hourToSendValue = null
    }

    emit('update:modelValue', {
      ...props.modelValue,
      delayUnit: delayUnitValue,
      sendAtSpecificHour: sendAtSpecificHourValue,
      hourToSend: hourToSendValue
    })
    emit('onChanges', { isCritical: true, field: 'delayUnit', value: delayUnitValue })
  }
}

const getDelayUnitIdFromValue = (e: DelayUnit) => {
  if (e === 'DAYS') {
    return 1
  } else if (e === 'HOURS') {
    return 2
  } else if (e === 'MINUTES') {
    return 3
  }
}

onMounted(() => {
  delayUnit.value = getDelayUnitIdFromValue(props.modelValue.delayUnit)

  const selectedHour = currentHoursOptions.value.find((option) => option.text === props.modelValue.hourToSend)
  hourSelected.value = selectedHour?.id

  if (props.triggerType === 'BIRTHDAY') {
    const updatedModelValue = {
      ...props.modelValue,
      sendImmediatlyAfterTrigger: false,
      sendAfterDelay: true,
      sendAtSpecificHour: true,
      daysBeforeAction: 0,
      delayUnit: 'DAYS' as DelayUnit
    }

    // If no hour is selected, select 09:00 by default
    if (!updatedModelValue.hourToSend) {
      const defaultHour = currentHoursOptions.value.find((option) => option.text === '09:00')
      if (defaultHour) {
        updatedModelValue.hourToSend = defaultHour.text
        updatedModelValue.hourToSendLabel = defaultHour.label
        hourSelected.value = defaultHour.id
      }
    }

    emit('update:modelValue', updatedModelValue)
  }
})
</script>

<template>
  <div class="flex min-h-20 w-full flex-row items-center border-b bg-muted px-5 py-3">
    <div class="flex flex-1 flex-col justify-center">
      <span class="font-semibold text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.delay.title') }}
      </span>
    </div>

    <div class="flex flex-row items-center">
      <Button variant="outline" class="mx-3" @click="emit('goPreviousStep')">
        <span class="text-foreground">{{ t('global.words.back') }}</span>
      </Button>
      <Button
        variant="default-primary"
        :disabled="!isDelayStepCompleted(modelValue, triggerType)"
        @click="emit('goNextStep')"
      >
        {{ t('global.words.continue') }}
      </Button>
    </div>
  </div>

  <div class="flex flex-col overflow-auto" style="height: calc(100% - 80px)">
    <!-- Interface normale pour les autres triggers -->
    <div v-if="triggerType !== 'BIRTHDAY'" class="flex w-full flex-col px-5 py-5">
      <div class="flex flex-col rounded-lg border bg-muted p-4">
        <p class="mb-4 font-medium text-foreground">
          {{ t('pages.marketingFlow.marketingFlowEditPage.delay.startAction') }}
        </p>
        <div class="flex flex-row">
          <div class="flex flex-1">
            <span class="text-sm">{{ t('pages.marketingFlow.marketingFlowEditPage.delay.immediatlyAfter') }}</span>
          </div>
          <BaseSwitch
            id="sendAtSpecificHour"
            :model-value="modelValue.sendImmediatlyAfterTrigger"
            @update:model-value="handleUpdateSendImmediatlyAfterTrigger"
          />
        </div>

        <div class="mt-4 flex flex-row">
          <div class="flex flex-1">
            <span class="text-sm">{{ t('pages.marketingFlow.marketingFlowEditPage.delay.delayBeforeAction') }}</span>
          </div>
          <BaseSwitch
            id="sendAtSpecificHour"
            :model-value="modelValue.sendAfterDelay"
            @update:model-value="handleUpdateSendAfterDelay"
          />
        </div>

        <div v-if="modelValue.sendAfterDelay === true" class="mt-2 flex flex-row gap-4">
          <AppFormInput
            id="field"
            :model-value="modelValue.daysBeforeAction"
            type="number"
            :min="1"
            class="flex-1"
            :errors="daysBeforeActionError"
            error-classes="text-xs"
            @update:model-value="handleUpdateDaysBeforeAction"
          />

          <ComboboxSelect
            value-key="id"
            label-key="text"
            :model-value="delayUnit ?? 1"
            :options="delayUnits"
            :search-threshold="4"
            trigger-class="w-28 h-10 items-center"
            @update:model-value="handleUpdateDelayUnit"
          />
        </div>

        <div v-if="delayUnit === 1 && modelValue.sendAfterDelay === true" class="mt-4 flex flex-row">
          <div class="flex flex-1">
            <span class="text-sm">{{ t('pages.marketingFlow.marketingFlowEditPage.delay.atSpecificTime') }}</span>
          </div>
          <BaseSwitch
            id="sendAtSpecificHour"
            :model-value="modelValue.sendAtSpecificHour"
            @update:model-value="handleUpdateSendAtSpecificHour"
          />
        </div>

        <ComboboxSelect
          v-if="delayUnit === 1 && modelValue.sendAtSpecificHour === true && modelValue.sendAfterDelay === true"
          value-key="id"
          label-key="text"
          :placeholder="t('pages.marketingFlow.marketingFlowEditPage.delay.selectHour')"
          :model-value="hourSelected"
          :options="currentHoursOptions"
          class="mt-2"
          trigger-class="w-full h-10 items-center"
          @update:model-value="handleUpdateHourToSend"
        />
      </div>
    </div>

    <div v-else class="flex w-full flex-col px-5 py-5">
      <div class="flex flex-col rounded-lg border bg-muted p-4">
        <p class="mb-4 font-medium text-foreground">
          {{ t('pages.marketingFlow.marketingFlowEditPage.delay.startAction') }}
        </p>

        <div class="mt-4">
          <label class="mb-2 block text-sm font-medium text-foreground">
            {{ t('pages.marketingFlow.marketingFlowEditPage.delay.atSpecificTime') }}
          </label>
          <ComboboxSelect
            value-key="id"
            label-key="text"
            :placeholder="t('pages.marketingFlow.marketingFlowEditPage.delay.selectHour')"
            :model-value="hourSelected"
            :options="currentHoursOptions"
            trigger-class="w-full h-10 items-center"
            @update:model-value="handleUpdateHourToSend"
          />
        </div>
      </div>
    </div>
  </div>
</template>
